import { AxiosError, AxiosPromise, AxiosRequestConfig, AxiosResponse } from "axios";
import { MockUtils } from "./MockUtils";

import { BackorderMockService } from "./BackorderMockService";
import { NewsMockService } from "./NewsMockService";
import { PartDetailMockService } from "./PartDetailMockService";

export class Mockios {
    public static defaultConfig(): Partial<IMockConfig<any>> {
        return {
            chaosMonkey: {
                probability: 0.05,
            },
        };
    }

    public static client(): Mockios {
        const routes: IMockRouteConfig[] = [];

        routes.push(...BackorderMockService.getRoutes());
        routes.push(...NewsMockService.getRoutes());
        routes.push(...PartDetailMockService.getRoutes());

        return new Mockios(Mockios.defaultConfig(), routes);
    }

    public static ok<T = any>(request: AxiosRequestConfig, data?: T): Promise<AxiosResponse<T>> {
        return Promise.resolve({
            status: 200,
            statusText: "OK",
            headers: [],
            config: request,
            request,
            data: data as T,
        });
    }

    public static fail<T = any>(request: AxiosRequestConfig, data?: any): Promise<AxiosResponse<T>> {
        return Promise.reject(
            Mockios.error(request, {
                data,
            })
        );
    }

    public static notFound<T = any>(request: AxiosRequestConfig, message?: string): Promise<AxiosResponse<T>> {
        return Promise.reject(
            Mockios.error(request, {
                status: 404,
                statusText: "Not Found",
                data: {
                    message: message || "Not found",
                },
            })
        );
    }

    public static unauthorized<T = any>(request: AxiosRequestConfig, message?: string): Promise<AxiosResponse<T>> {
        return Promise.reject(
            Mockios.error(request, {
                status: 401,
                statusText: "Unauthorized",
                data: {
                    message: message || "Unauthorized",
                },
            })
        );
    }

    public static error<T = any>(request: AxiosRequestConfig, response: Partial<AxiosResponse<T>>): AxiosError {
        const defaultResponse = MockUtils.errorResponse(request, "An error occurred", 500, "Internal Server Error");

        const fullResponse = {
            ...defaultResponse,
            ...response,
        };

        return {
            name: "Mockios Error",
            message: fullResponse.statusText,
            config: request,
            code: String(response.status),
            request,
            response: fullResponse,
        };
    }

    private config: IMockConfig;
    private routes?: IMockRouteConfig[];

    constructor(defaultConfig?: Partial<IMockConfig>, routes?: IMockRouteConfig[]) {
        this.config = {
            result: Promise.resolve(undefined),
            ...defaultConfig,
        };
        this.routes = routes;
    }

    public async request<T = any>(request: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        let cfg = this.config;

        if (this.routes) {
            const routeConfig = this.routes.find((x) => x.isMatch(request));

            if (routeConfig) {
                cfg = {
                    ...cfg,
                    ...routeConfig,
                };
            }
        }

        const randoCalrissian = Math.random();

        await MockUtils.delay((cfg.maxDelay || 3000) * randoCalrissian);

        if (MockUtils.shouldChaosMonkeyAppear(this.config, randoCalrissian)) {
            return Promise.reject(await MockUtils.chaosMonkeyResult(request, cfg.chaosMonkey!));
        }

        const response = await this.getMockResponse<T>(request, cfg);

        return response;
    }

    public get<T = any>(url: string, config?: AxiosRequestConfig | undefined): AxiosPromise<T> {
        return this.request(
            MockUtils.merge(config, {
                method: "get",
                url,
            })
        );
    }

    public delete(url: string, config?: AxiosRequestConfig | undefined): AxiosPromise<any> {
        return this.request(
            MockUtils.merge(config, {
                method: "delete",
                url,
            })
        );
    }

    public head(url: string, config?: AxiosRequestConfig | undefined): AxiosPromise<any> {
        return this.request(
            MockUtils.merge(config, {
                method: "head",
                url,
            })
        );
    }

    public post<T = any>(url: string, data?: any, config?: AxiosRequestConfig | undefined): AxiosPromise<T> {
        return this.request(
            MockUtils.merge(config, {
                method: "post",
                url,
                data,
            })
        );
    }

    public put<T = any>(url: string, data?: any, config?: AxiosRequestConfig | undefined): AxiosPromise<T> {
        return this.request(
            MockUtils.merge(config, {
                method: "put",
                url,
                data,
            })
        );
    }

    public patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig | undefined): AxiosPromise<T> {
        return this.request(
            MockUtils.merge(config, {
                method: "patch",
                url,
                data,
            })
        );
    }

    private getMockResponse<T>(request: AxiosRequestConfig, cfg: IMockConfig<T>) {
        const fn = cfg.result as resultFactory<T>;

        if (fn !== null && typeof fn === "function") {
            return fn(request);
        }

        return Mockios.ok(request, cfg.result as T);
    }
}

export type resultFactory<T> = (request?: AxiosRequestConfig) => Promise<AxiosResponse<T>>;

export interface IMockConfig<T = any> {
    result: any | resultFactory<T>;
    maxDelay?: number;
    chaosMonkey?: IChaosMonkeyConfig;
    stopTheMonkey?: boolean;
}

export interface IChaosMonkeyConfig<T = any> {
    probability?: number;
    whatDidTheMonkeyDo?: string | resultFactory<T>;
}

export interface IMockRouteConfig<T = any> extends IMockConfig<T> {
    isMatch: (request: AxiosRequestConfig) => boolean;
}
